import type { User } from 'utils/models'
import { GroupedContacts } from './types'

/**
 * Format phone number by removing +97 prefix and showing only last 3 digits
 * @param phone - The phone number to format
 * @returns Formatted phone number with asterisks
 */
export const formatPhoneNumber = (phone: string): string => {
  // Remove +97 prefix
  let formatted = phone.replace(/^\+97/, '')

  // If the number starts with 0, remove it
  if (formatted.startsWith('0')) {
    formatted = formatted.substring(1)
  }

  // Show asterisks for all digits except last 3
  if (formatted.length > 3) {
    const lastThree = formatted.slice(-3)
    const asterisks = '*'.repeat(formatted.length - 3)
    return lastThree + asterisks
  }

  return formatted
}

/**
 * Group contacts by first letter of their first name
 * @param contacts - Array of user contacts
 * @param searchQuery - Optional search query to filter contacts
 * @returns Grouped contacts object
 */
export const groupContactsByLetter = (
  contacts: User[],
  searchQuery?: string
): GroupedContacts => {
  const filtered = searchQuery
    ? contacts.filter(contact =>
      contact.given_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.family_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.phones?.some(phone => phone.includes(searchQuery))
    )
    : contacts

  const grouped: GroupedContacts = {}
  filtered.forEach(contact => {
    const firstLetter = contact.given_name?.charAt(0).toUpperCase() || 'א'
    if (!grouped[firstLetter]) {
      grouped[firstLetter] = []
    }
    grouped[firstLetter].push(contact)
  })

  return grouped
}

/**
 * Handle scrolling for person display carousels
 * @param direction - 'left' or 'right'
 * @param currentPosition - Current scroll position
 * @param totalItems - Total number of items
 * @param visibleItems - Number of visible items
 * @returns New scroll position
 */
export const handlePersonCarouselScroll = (
  direction: 'left' | 'right',
  currentPosition: number,
  totalItems: number,
  visibleItems: number
): number => {
  if (direction === 'left') {
    return Math.max(0, currentPosition - 1)
  } else {
    const maxScroll = Math.max(0, totalItems - visibleItems)
    return Math.min(maxScroll, currentPosition + 1)
  }
}

/**
 * Get visible contacts from a selected set based on scroll position
 * @param selectedIds - Set of selected contact IDs
 * @param scrollPosition - Current scroll position
 * @param numberOfItems - Number of items to display
 * @returns Array of visible contact IDs
 */
export const getVisibleContacts = (
  selectedIds: Set<number>,
  scrollPosition: number,
  numberOfItems: number
): number[] => {
  const selectedArray = Array.from(selectedIds)
  return selectedArray.slice(scrollPosition, scrollPosition + numberOfItems)
}

// Generate a unique GUID
export const generateGUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}

// Helper function to get icon type from title
export const getIconTypeFromTitle = (title: string): 'like' | 'share' | 'birthday' | 'coupon' => {
  const lowerTitle = title.toLowerCase()

  if (lowerTitle.includes('לייק') || lowerTitle.includes('אהב') || lowerTitle.includes('like')) {
    return 'like'
  }

  if (lowerTitle.includes('שיתפ') && lowerTitle.includes('איתך')) {
    return 'share'
  }

  if (lowerTitle.includes('היום הגדול') || lowerTitle.includes('יום הולדת')) {
    return 'birthday'
  }

  return 'coupon'
}


